<script>
    let process = "client";
    import { beforeUpdate, createEventDispatcher, onMount } from 'svelte';
    import { sortByLabel } from '../../shared/utils/Sort.svelte'
    import Fuse from 'fuse.js';
    import { fade } from 'svelte/transition';
	import Paginate from './Paginate.svelte';
	import { debounce } from './debounce';
	import * as local from './local';
	import { collect, exportExcel, print } from './data-grid';

    import { immerStore } from './store';

	export let modal;
	let source = modal.source;
	export let clickable = true;
	export let printable = true;
	export let exportable = true;
	export let searchable = true;
	export let searching = false;
	export let searchInputRef = null;
	export let searchInput = '';

	export let sortable = true;
	export let sortColumn = -1;
	export let sortType = 'asc';

	export let currentPerPage = 1000;
	export let perPageOptions = [];
	export let currentPage = 1;
	export let rowCount = 0;
	export let pageCount = 0;
    export let selected = 0;
	export let perPage = [1000, 5000, 10000];
	export let paginate = { size: perPage[0], page: 1 };
	export let defaultPerPage = null;
	export let exactSearch = true;		
	export let tooltips;

    let columns = [];

	// local
    let rows = [];
    let title = "";
	export let paginatedRows = [];
	// server
	export let paged = { paginate: paginate };

	const dispatch = createEventDispatcher();

    const paginated = immerStore({
		paginatedRows,
		rows,
		paginate,
		size: currentPerPage,
		page: currentPage,
    });
    
	export function search(e) {
		e.currentTarget.blur();
		searching = !searching;
		if (searching) {
			setTimeout(() => searchInputRef.focus(), 100);
		}
	}

	export function setSortIcon(index) {
		if (!sortable)
			return;
		if (sortColumn === index) {
			sortType = sortType === 'asc' ? 'desc' : 'asc';
		} else {
			sortType = 'asc';
			sortColumn = index;
		}
		sortType = sortType, sortColumn = sortColumn;
	}

	export function setPerPageOptions() {
		let options = perPage;
		// Force numbers
		options = options.map( v => parseInt(v));		
		// Set current page to first value
		currentPerPage = options[0];	
		// Sort options
		options.sort((a,b) => a - b);	
		// And add "All"
		options.push(-1);	
		// If defaultPerPage is provided and it's a valid option, set as current per page
		if (options.indexOf(defaultPerPage) > -1) {
			currentPerPage = parseInt(defaultPerPage);
		}	
		currentPerPage = currentPerPage, perPageOptions = options;
	}

	export function click(row) {
		if (!clickable) {
			return;
		}
		if (getSelection().toString()) {
			// Return if some text is selected instead of firing the row-click event.
			return;
		}
		dispatch('row-click', row);
		var idField = "_id";
		var layer = modal.layer;
		if (layer) {
			if (layer.id == "reportlog" || layer.id == "reportlog_line" || layer.id == "reportlog_fill") {
				idField = "history_id";
			}
			else {
				if (layer.metadata && layer.metadata["geosync:id"])
					idField = layer.metadata["geosync:id"];
			}
			var feature = source.features.find(x => x.properties[idField] === row[idField]);
			if (feature) {
				dispatch('message', {
						type: "grid",
						data: {
							feature: feature,
							layer: layer
						}
				});
			}
		}
		// console.log('click', row);
	}

	export function sort(index) {
		if (index > -1) {
			setSortIcon(index);
			getPaged({colName: columns[index].field, direction: sortType});				
		}
	}

	onMount(() => {
        if (source) {
            columns = [];
            rows = [];
            title = "";
            if (source.name)
                title = source.name;
            var columnNames = {};
            source.features.map(function (feature) {
                Object.keys(feature.properties).map(function (k) {
                    columnNames[k] = {
                        label: k,
                        field: k,
                        numeric: typeof feature.properties[k] === "number" ? true : false,
                        html: true
					}
                });
                rows.push(feature.properties);
            })
            columns = Object.values(columnNames).sort(sortByLabel);
            setPerPageOptions();		
        }	
	});

	function getPagedLocal(props, pred) { 		
		const values = local.getPaged(props, pred, {	
			paginate, selected,
			currentPage, currentPerPage,
            sortable, sortColumn, sortType, 
			searchInput, exactSearch,
			rowCount, pageCount, 
			rows, columns	
		});
		({	paginate, selected,
			currentPage, currentPerPage,
            sortable, sortColumn, sortType,
			searchInput, exactSearch,
			rowCount, pageCount,
			rows, paginatedRows
		} = values);
		update(paginatedRows, currentPerPage, selectedPage);
	}

	function getPaged(props, pred) {
		getPagedLocal(props, pred);
	}	
	
	$: selectedPage = selected + 1;
	let previous;	
	function update(paginatedRows, size, page) {
		paginated.update($paginated => {
			$paginated.paginatedRows = paginatedRows;
			$paginated.rows = rows;
			$paginated.size = size;
			$paginated.page = page;
		});
		previous = $paginated;
	}

	$: {
		// console.log('$paginated - previous - selectedPage ', $paginated.page, previous ? previous.page : -1, selectedPage);
		if (previous) {
			if (rows !== previous.rows) {
				getPaged({ rows });
			}
			if (currentPerPage !== previous.size) {
				getPaged({ size: currentPerPage });
			}
			if (selectedPage !== previous.page) {
				getPaged({ page: selectedPage }, x => x.page != selectedPage);
				paginate.page = selectedPage;
			}
		}
		previous = $paginated;
	}

	let prevPaged;
	$: if (paged.rows && paged !== prevPaged) {
		updatePaged(paged);
		prevPaged = paged;
	}

	let prevSearchInput = '';
	beforeUpdate(() => {			        
		if (searchInput != prevSearchInput) {
			debounce(() => {
				if (searchInput != prevSearchInput) {
					getPaged({ searchText: searchInput });
					prevSearchInput = searchInput;
				}
			}, 400)();
		}	        
    });
    
	function sendMessage(message) {
		dispatch('message', message);
	}
	
	function close() {
		dispatch('message', {
            type: "modal",
            modal: null
		});
    }
</script>

<div class="modal modal-lg active">
    <div class="modal-overlay" on:click={close} aria-label="Close"></div>
    <div id="modal-container" class="modal-container">
        <div class="modal-header">
            <div class="tile tile-centered">
                <div class="tile-content">
                    <div class="tile-title">{title}</div>
                </div>
                <div class="tile-action">
                    {#if printable}
					<button
						class="btn btn-link {tooltips ? "tooltip tooltip-left" : ""}" data-tooltip="Print"
						on:click="{(e) => {
							e.currentTarget.blur();
							print(columns, rows)
						}}">
						<i class="icon icon-bookmark"></i>
					</button>
					{/if}
					{#if exportable}
					<button
						class="btn btn-link {tooltips ? "tooltip tooltip-left" : ""}" data-tooltip="Download"
						v-if="this.exportable"
						on:click="{(e) => {
							e.currentTarget.blur();
							if (!title && modal.layer)
								title = modal.layer.id;
							exportExcel(columns, rows, title)
						}}">
						<i class="icon icon-download"></i>
					</button>
					{/if}
					{#if searchable}
					<button
						class="btn btn-link {tooltips ? "tooltip tooltip-left" : ""}" data-tooltip="Search"
						on:click="{search}">
						<i class="icon icon-search"></i>
					</button>
					{/if}
                    <button class="btn btn-link {tooltips ? "tooltip tooltip-left" : ""}" data-tooltip="Close" on:click={close} aria-label="Close"><i class="icon icon-cross mt-1"></i></button>
                </div>
            </div>
			{#if searching}
				<div>
					<div id="search-input-container">
						<label>
							<input type="search" id="search-input" bind:this={searchInputRef}
								class="form-input" placeholder="Search data"
								bind:value="{searchInput}">
						</label>
					</div>
				</div>
			{/if}
        </div>
        <div class="modal-body">
			<table ref="table"
					class="table table-striped table-hover table-scroll">
				<thead>
					<tr>
						{#each columns as column, x}
						<th on:click="{() => sort(x)}"
							class="{ sortable ? 'sorting ' : ''}
								{ sortColumn === x ?
									(sortType === 'desc' ? 'sorting-desc' : 'sorting-asc')
									: '' }
								{ column.numeric ? ' numeric' : ''}"
							style="cursor:pointer;width: { column.width ? column.width : 'auto' }">
							{column.label}
							{#if sortColumn === x && sortType === 'desc'}
								<i class="icon icon-downward"></i>
							{:else if sortColumn === x && sortType === 'asc'}
								<i class="icon icon-upward"></i>
							{/if}
							<i class="icon icon-upward"></i>
						</th>
						{/each}
					</tr>
				</thead>

				<tbody>
					{#each $paginated.paginatedRows as row, y}
						<tr class="{ clickable ? 'clickable' : '' }" on:click="{() => click(row)}">
							{#each columns as column, x}
								<td>
									{#if column.html}
										{@html collect(row, column.field)}
									{:else}
										{ collect(row, column.field) }   
									{/if}     
								</td>    
							{/each}
						</tr>
					{/each}
				</tbody>
			</table>
        </div>
		{#if paginate}
			<div class="modal-footer">
				<div class="tile tile-centered">
					<div class="tile-icon">
						<label>
							<span>Rows per page:</span>
							<select class="form-select" style="width:auto;" bind:value="{currentPerPage}">
								{#each perPageOptions as option, x}
									<option value={option}>
									{ option === -1 ? 'All' : option }
									</option>
								{/each}
							</select>
						</label>
					</div>
					<div class="tile-content text-center">
						{(currentPage - 1) * currentPerPage ? (currentPage - 1) * currentPerPage : 1}
							- {Math.min(rowCount, currentPerPage * currentPage)} of {rowCount}
					</div>
					<div class="tile-action">
						<Paginate
							bind:pageCount={pageCount}
							marginPages="2"
							pageRange="4"
							containerClass="pagination"
							pageLinkClass="page-item"
							prevClass="page-item"
							nextClass="page-item"
							bind:selected="{selected}"
							activeClass="active"
							>			
						</Paginate>
					</div>
				</div>
			</div>
		{/if}
    </div>
</div>

<style>
	.table.table-scroll {
		padding-bottom: 0;
		height: 100%;
	}
	tr.clickable {
		cursor: pointer;
	}
	table {
		table-layout: fixed;
        width: 100%;
	}
	table tr td {
		padding: 0 0 0 56px;
		height: 48px;
		font-size: 13px;
		color: rgba(0, 0, 0, 0.87);
		border-bottom: solid 1px #DDDDDD;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	table td, table th {
    	border-radius: 0;
	}
	table tr td a i {
		font-size: 18px;
		color: rgba(0, 0, 0, 0.54);
	}
	table tr {
		font-size: 12px;
	}
	table th {
		font-size: 12px;
		font-weight: 500;
		color: #757575;
		cursor: pointer;
		white-space: nowrap;
		padding: 0;
		height: 2rem;
		padding-left: 56px;
		vertical-align: middle;
		outline: none !important;
	    overflow: hidden;
	    text-overflow: ellipsis;
	}
	table tbody tr:hover {
		background-color: #EEE;
	}
	
	table th:last-child,
	table td:last-child {
		padding-right: 14px;
	}
	table th:first-child, table td:first-child {
		padding-left: 24px;
	}
    .modal.modal-lg .modal-overlay {
        background: rgba(247, 248, 249, 0.75);
    }
    .modal.modal-lg .modal-container {
        height: 100vh;
        height: calc(var(--vh, 1vh) * 100);
        padding: 0;
    }
    .modal.modal-lg .modal-header {
        background-color: #00ab5f;
        color: #fff;
        padding: 0.2rem 0.4rem;
	}
	.modal.modal-lg .modal-footer {
		background-color: #efefef;
        padding: 0;
    }
    .modal.modal-lg .modal-header .btn.btn-link {
        color: white;
    }
    .modal.modal-lg .modal-body {
        padding: 0;
        height: calc(100vh - 4.2rem);
    }
    ::-webkit-scrollbar {
		width: 6px;
		background-color: #f7f8f9;
	}
	::-webkit-scrollbar-thumb {
		background-color: rgba(122,129,132, 1);
    }
</style>